import { Alert } from "@gigpro/ui/Alert";
import { Button } from "@gigpro/ui/Button";
import { Cycle } from "@gigpro/ui/Icons";
import { Skeleton } from "@gigpro/ui/Skeleton";
import type { HireFee, JobPostingResponse } from "@sidegigllc/api/client";
import { useJobCreditStats } from "api/queries/hire";
import { hireFeePricing, jobsPlanHireFeeMessage } from "domains/hire/hireFeePlan";
import { useHireFeePricing } from "domains/hire/hooks/useHireFeePricing";
import { useJobsSubscription } from "domains/hire/hooks/useJobsSubscription";
import { formatCents } from "domains/hire/utils/hireFee";
import type React from "react";
import { useActiveOrganization } from "services/locationOrgContext";
import { type JobOfferPricing, JobsPlanPaymentSummary, PaymentSummary } from "../../components/PaymentSummary";

/** What the summary needs of a job; a direct hire has no job yet, so no display id. */
type SummaryJob = Pick<JobPostingResponse, "location" | "name"> & { display_id?: string };

interface PaymentSummaryContainerProps {
  job: SummaryJob;
  hireFee?: HireFee;
  isLoadingHireFee?: boolean;
  proFirstName?: string;
}

const LoadingSummary = () => (
  <div className="flex flex-col">
    <Skeleton className="h-48 w-full" />
    <Skeleton className="h-10 w-full" />
  </div>
);

export const PaymentSummaryContainer: React.FC<PaymentSummaryContainerProps> = ({
  job,
  hireFee,
  isLoadingHireFee,
  proFirstName,
}) => {
  const jobsSubscription = useJobsSubscription(job.location);
  if (isLoadingHireFee) return <LoadingSummary />;
  const pricing = hireFeePricing(hireFee);
  const jobsPlanMessage = jobsPlanHireFeeMessage(pricing, proFirstName, jobsSubscription);
  if (jobsPlanMessage) {
    // The summary's own row already says the hire fee is $0, so only the offer to subscribe needs a line.
    const message = pricing === "subscribed" ? undefined : jobsPlanMessage;
    return <JobsPlanPaymentSummary jobDisplayId={job.display_id} jobName={job.name} message={message} />;
  }
  return <LegacyPaymentSummaryContainer job={job} hireFee={hireFee} />;
};

const LegacyPaymentSummaryContainer: React.FC<{ job: SummaryJob; hireFee?: HireFee }> = ({ job, hireFee }) => {
  const { data: creditStats, isLoading, isError, error, refetch } = useJobCreditStats();
  const organization = useActiveOrganization();
  const pricing = useHireFeePricing({
    locationId: job.location,
    organizationId: organization?.id,
    enabled: !hireFee,
  });

  if (isLoading) return <LoadingSummary />;

  if (isError || !creditStats) {
    return (
      <Alert
        severity="error"
        action={
          <Button variant="destructive" className="inline-flex items-center gap-1" onClick={() => refetch()}>
            <Cycle className="size-3" />
            Retry
          </Button>
        }
      >
        {error?.message ?? "Unable to load payment details"}
      </Alert>
    );
  }

  const baseFeeCents = hireFee?.base_fee_cents ?? pricing.baseAmount.cents;
  const feeCents = hireFee?.fee_cents ?? pricing.baseAmount.cents;
  const willUseFreeCredit = creditStats.free_available > 0;
  const dueCents = willUseFreeCredit ? 0 : feeCents;

  const offerPricing: JobOfferPricing = {
    postingCostDisplay: "Free",
    hireFeeDisplay: formatCents(baseFeeCents),
    offerHireFeeDisplay: formatCents(feeCents),
    discount:
      hireFee && hireFee.discount_cents > 0
        ? {
            label: `Discount (${Math.round(hireFee.hours_worked_together)} hrs worked)`,
            valueDisplay: `-${formatCents(hireFee.discount_cents)}`,
          }
        : undefined,
    creditAppliedDisplay: formatCents(feeCents),
    totalDueTodayDisplay: formatCents(dueCents),
    noChargeDue: dueCents === 0,
  };

  return (
    <PaymentSummary jobDisplayId={job.display_id} jobName={job.name} pricing={offerPricing} credits={creditStats} />
  );
};
